import React from "react";
import {observer} from "mobx-react-lite";
import {Link, useLocation, useNavigate} from "react-router-dom";

import Select from "../ui/Select";

import clubStore from "../storage/ClubStore";
import appStore from "../storage/AppStore";
import newStore from "../storage/NewStore";

import {getMenu as getMenuData} from "../common/menu";
import {isCourse1} from "../common/config";

import logoClub1 from "../images/logo.webp";
import logoCourse1 from "../images/logo-course1.webp";


const logo = isCourse1()
    ? logoCourse1
    : logoClub1;


const Menu = ({screen, onClose}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const menuData = getMenuData();


    /* =====================================================
       LANGUAGE
       ===================================================== */

    const selectedLanguageTemplate = (option, props) => {
        if (option) {
            return <b>{option.icon}</b>;
        }

        return <span>{props.placeholder}</span>;
    };


    const languageOptionTemplate = (option, props) => {
        if (option) {
            return (
                <div className="flex align-items-center">
                    <span>
                        {option.icon} {option.label}
                    </span>
                </div>
            );
        }

        return <span>{props.placeholder}</span>;
    };


    /* =====================================================
       HELPERS
       ===================================================== */

    const isActiveUrl = (url) => {
        if (!url) return false;

        if (url === "/") {
            return location.pathname === "/";
        }

        return (
            location.pathname === url ||
            location.pathname.startsWith(`${url}/`)
        );
    };


    const handleNavigate = (url) => {
        if (!url) return;

        navigate(url);
        onClose?.();
    };


    const handleSelectCompany = (companyId) => {
        clubStore.getClub(companyId);
        onClose?.();
    };


    const getRoleMenu = (role) => {
        return menuData.find((item) => item.role === role);
    };


    /* =====================================================
       MENU ITEMS
       ===================================================== */

    const MenuItems = ({items = [], showCompanies = false}) => {
        return (
            <ul className="menu-list">
                {showCompanies &&
                    appStore.user?.companies?.map((cc) => {
                        const selected =
                            clubStore.club?.id === cc.company.id;

                        return (
                            <li
                                key={cc.company.id}
                                className={`menu-item${selected ? " active" : ""}`}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                    handleSelectCompany(cc.company.id)
                                }
                                onKeyDown={(event) => {
                                    if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                    ) {
                                        event.preventDefault();

                                        handleSelectCompany(
                                            cc.company.id
                                        );
                                    }
                                }}
                            >
                                <i
                                    className={
                                        selected
                                            ? "pi pi-check-circle"
                                            : "pi pi-circle"
                                    }
                                />

                                <span>
                                    {cc.company.name}
                                </span>
                            </li>
                        );
                    })
                }


                {items.map((item) => {
                    const active = isActiveUrl(item.url);

                    return (
                        <li
                            key={`${item.url}-${item.label}`}
                            className={`menu-item${active ? " active" : ""}`}
                            role="button"
                            tabIndex={0}
                            aria-current={active ? "page" : undefined}
                            onClick={() =>
                                handleNavigate(item.url)
                            }
                            onKeyDown={(event) => {
                                if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                ) {
                                    event.preventDefault();

                                    handleNavigate(item.url);
                                }
                            }}
                        >
                            {item.icon && (
                                <i className={item.icon}/>
                            )}

                            <span>
                                {appStore.tr[item.label] || item.label}
                            </span>
                        </li>
                    );
                })}
            </ul>
        );
    };


    /* =====================================================
       DESKTOP SECTION
       ===================================================== */

    const DesktopMenuTemplate = ({role}) => {
        const item = getRoleMenu(role);

        if (!item) {
            return null;
        }

        const name =
            role === "company"
                ? clubStore.club?.name
                : (
                    appStore.tr[item.label] ||
                    item.label
                );

        return (
            <section className="menu-section">
                <div className="menu-section-header">
                    {item.icon && (
                        <i className={item.icon}/>
                    )}

                    <span>{name}</span>
                </div>


                <MenuItems items={item.items || []}/>


                {role === "common" && (
                    <ul className="menu-list">
                        <li
                            className="menu-item"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                sessionStorage.removeItem("jwt");

                                window.location.href =
                                    `${window.location.origin}/`;
                            }}
                        >
                            <i className="pi pi-sign-out"/>

                            <span>
                                {appStore.tr.logout || "Выйти"}
                            </span>
                        </li>
                    </ul>
                )}
            </section>
        );
    };


    /* =====================================================
       COMPANY SELECTION
       ===================================================== */

    const CompaniesSelection = () => {
        return (
            <section className="menu-section">
                <div className="menu-section-header">
                    <i className="pi pi-briefcase"/>

                    <span>
                        {appStore.tr.please_select}
                    </span>
                </div>


                <ul className="menu-list">
                    {appStore.user.companies?.map((cc) => {
                        const selected =
                            clubStore.club?.id === cc.company.id;

                        return (
                            <li
                                key={cc.company.id}
                                className={`menu-item${selected ? " active" : ""}`}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                    handleSelectCompany(cc.company.id)
                                }
                            >
                                <i
                                    className={
                                        selected
                                            ? "pi pi-check-circle"
                                            : "pi pi-circle"
                                    }
                                />

                                <span>
                                    {cc.company.name}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </section>
        );
    };


    /* =====================================================
       NEW COMPANY
       ===================================================== */

    const NewCompany = () => {
        const active = isActiveUrl("/add");

        return (
            <section className="menu-section">
                <div className="menu-section-header">
                    <i className="pi pi-building"/>

                    <span>
                        {appStore.tr.welcome}
                    </span>
                </div>


                <ul className="menu-list">
                    <li
                        className={`menu-item${active ? " active" : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            newStore.resetCompany();
                            handleNavigate("/add");
                        }}
                    >
                        <i className="pi pi-file-edit"/>

                        <span>
                            {appStore.tr.add_company}
                        </span>
                    </li>
                </ul>
            </section>
        );
    };


    /* =====================================================
       USER
       ===================================================== */

    const UserInfo = () => {
        if (!appStore.user?.id) {
            return null;
        }

        return (
            <div className="sheet-user">
                {appStore.user.avatar ? (
                    <img
                        src={appStore.user.avatar}
                        className="avatar"
                        alt={appStore.user.fio || "User"}
                    />
                ) : (
                    <span className="avatar avatar-fallback">
                        <i className="pi pi-user"/>
                    </span>
                )}

                <span>
                    {appStore.user.fio}
                </span>
            </div>
        );
    };


    /* =====================================================
       DESKTOP
       ===================================================== */

    if (screen === "desktop") {
        return (
            <>
                <div className="menu-header">
                    <Link
                        to="/"
                        className="menu-logo"
                    >
                        <img
                            src={logo}
                            height="28"
                            alt="Logo"
                        />
                    </Link>


                    <div className="sheet-lang">
                        <Select
                            store={appStore.user}
                            prop="language"
                            options={appStore.languages}
                            label={appStore.tr.language}
                            valueTemplate={selectedLanguageTemplate}
                            itemTemplate={languageOptionTemplate}
                            onChange={(value) =>
                                appStore.setLanguage(value)
                            }
                            notFull
                            className="lang-select"
                        />
                    </div>
                </div>


                <UserInfo/>


                <div className="sidebar-menu">
                    {appStore.user.id > 0 &&
                        !appStore.user.is_admin &&
                        appStore.user.companies?.length === 0 && (
                            <NewCompany/>
                        )
                    }


                    {appStore.user.id > 0 &&
                        appStore.user.companies?.length > 1 && (
                            <CompaniesSelection/>
                        )
                    }


                    {clubStore.club?.id > 0 && (
                        <DesktopMenuTemplate role="company"/>
                    )}


                    {appStore.user.is_admin && (
                        <DesktopMenuTemplate role="admin"/>
                    )}


                    {clubStore.club?.is_manager && (
                        <DesktopMenuTemplate role="manager"/>
                    )}


                    {clubStore.club?.is_trainer && (
                        <DesktopMenuTemplate role="trainer"/>
                    )}


                    {clubStore.club?.is_parent && (
                        <DesktopMenuTemplate role="parent"/>
                    )}


                    {clubStore.club?.is_member && (
                        <DesktopMenuTemplate role="member"/>
                    )}


                    {appStore.user.id > 0 ? (
                        <DesktopMenuTemplate role="common"/>
                    ) : (
                        <DesktopMenuTemplate role="anonymous"/>
                    )}
                </div>
            </>
        );
    }


    /* =====================================================
       MOBILE
       ===================================================== */

    if (screen === "mobile") {
        const selectedRole =
            getRoleMenu(clubStore.selectedMenu);

        const menuItems =
            selectedRole?.items || [];

        const showCompanies =
            clubStore.selectedMenu === "company";


        return (
            <>
                <div className="menu-header">
                    <Link
                        to="/"
                        className="menu-logo"
                        onClick={onClose}
                    >
                        <img
                            src={logo}
                            height="28"
                            alt="Logo"
                        />
                    </Link>


                    <span className="menu-header-title">
                        {selectedRole
                            ? (
                                appStore.tr[selectedRole.label] ||
                                (
                                    selectedRole.role === "company"
                                        ? clubStore.club?.name
                                        : selectedRole.label
                                )
                            )
                            : clubStore.club?.name
                        }
                    </span>


                    <div className="sheet-lang">
                        <Select
                            store={appStore.user}
                            prop="language"
                            options={appStore.languages}
                            label={appStore.tr.language}
                            valueTemplate={selectedLanguageTemplate}
                            itemTemplate={languageOptionTemplate}
                            onChange={(value) =>
                                appStore.setLanguage(value)
                            }
                            notFull
                            className="lang-select"
                        />
                    </div>
                </div>


                <UserInfo/>


                <MenuItems
                    items={menuItems}
                    showCompanies={showCompanies}
                />
            </>
        );
    }


    return null;
};


export default observer(Menu);